'use client'

import { EnvelopeClosedIcon } from '@radix-ui/react-icons'
import { useLocale } from 'next-intl'
import { useMemo } from 'react'
import { Button } from '@/components/button'
import { Motion } from '@/components/motion'
import { T } from '@/components/t'
import { ConsentGate } from '@/features/gdpr/consent-gate'
import { MoinAiChatPlaceholder } from '@/features/gdpr/placeholders/moin-ai-chat-placeholder'
import { THIRD_PARTY_PURPOSE } from '@/features/gdpr/use-purpose-consent'
import { Language, Locale, parseLanguageFromLocale } from '@/setup/locales'
import { cn } from '@/utils/styles/cn'

export const ContactLandingPage = ({
    hideTitle,
    fullHeightChat,
}: {
    hideTitle?: boolean
    fullHeightChat?: boolean
}) => {
    const locale = useLocale()

    const language = parseLanguageFromLocale(locale as Locale)

    const isGermanChatShown = language === Language.DE
    const isEnglishChatShown = language === Language.EN

    const isChatShown = isGermanChatShown || isEnglishChatShown

    const supportEmail = useMemo(
        () =>
            ({
                externalUrl: 'mailto:support@neotaste.app',
            }) as const,
        []
    )

    return (
        <section
            className={cn(
                'flex w-full flex-col items-center',
                fullHeightChat ? 'px-0 py-0 mb-0' : 'py-7 md:py-12 px-4 mb-40 max-w-[700px]'
            )}
        >
            {!hideTitle && (
                <Motion baseDelay={0.25} y={55}>
                    <h1
                        className={cn(
                            'text-[36px] md:text-[40px] lg:text-[44px] xl:text-[48px] font-bold text-center',
                            isChatShown ? '' : 'mb-8'
                        )}
                    >
                        <T span t={'contact.hero.title'} />
                    </h1>
                </Motion>
            )}

            {isGermanChatShown && (
                <Motion
                    baseDelay={0.45}
                    y={70}
                    className={cn('flex flex-col items-center w-full', !fullHeightChat && 'mb-12')}
                >
                    <ConsentGate
                        integrationId="moin-ai"
                        purposeId={THIRD_PARTY_PURPOSE}
                        placeholder={(activate) => (
                            <MoinAiChatPlaceholder
                                onActivate={activate}
                                className={cn('w-full', !fullHeightChat && 'rounded-lg')}
                                style={{ height: fullHeightChat ? '100dvh' : '500px' }}
                            />
                        )}
                    >
                        <iframe
                            src="/_html/german-moin-ai-iframe.html"
                            width="100%"
                            height={fullHeightChat ? undefined : '500px'}
                            frameBorder="0"
                            style={fullHeightChat ? { height: '100dvh' } : undefined}
                            className={cn(
                                'moin-iframe animate-none',
                                !fullHeightChat && 'rounded-lg'
                            )}
                        />
                    </ConsentGate>
                </Motion>
            )}

            {isEnglishChatShown && (
                <Motion
                    baseDelay={0.45}
                    y={70}
                    className={cn('flex flex-col items-center w-full', !fullHeightChat && 'mb-12')}
                >
                    <ConsentGate
                        integrationId="moin-ai"
                        purposeId={THIRD_PARTY_PURPOSE}
                        placeholder={(activate) => (
                            <MoinAiChatPlaceholder
                                onActivate={activate}
                                className={cn('w-full', !fullHeightChat && 'rounded-lg')}
                                style={{ height: fullHeightChat ? '100dvh' : '500px' }}
                            />
                        )}
                    >
                        <iframe
                            src="/_html/english-moin-ai-iframe.html"
                            width="100%"
                            height={fullHeightChat ? undefined : '500px'}
                            frameBorder="0"
                            style={fullHeightChat ? { height: '100dvh' } : undefined}
                            className={cn(
                                'moin-iframe animate-none',
                                !fullHeightChat && 'rounded-lg'
                            )}
                        />
                    </ConsentGate>
                </Motion>
            )}

            <Motion
                baseDelay={isChatShown ? 0.65 : 0.45}
                y={isChatShown ? 85 : 70}
                className={cn('flex flex-col items-center', fullHeightChat && 'px-4 py-7')}
            >
                {isChatShown ? (
                    <T
                        p
                        t="contact.hero.description"
                        className="font-normal  mb-6 text-black/70 text-center text-[14px] md:text-[16px]"
                    />
                ) : (
                    <T
                        p
                        t="contact.hero.descriptionOld"
                        className="font-normal  mb-6 text-black/70 text-center text-[14px] md:text-[16px]"
                    />
                )}

                <Button
                    shape="pill"
                    width="max-content"
                    height="46px"
                    styleType="primary"
                    href={supportEmail}
                    target="_blank"
                >
                    <EnvelopeClosedIcon className="w-5 h-5 mr-1" />
                    support@neotaste.app
                </Button>
            </Motion>
        </section>
    )
}
